/* eslint-disable max-lines */
import * as data from "shared/data";
import { config } from "shared/data";
import { Edition } from "shared/lib/edition/data"; // This is for preventing server restart because of changing hooks
import { PageType } from "shared/lib/page-type";

import type { AppBarVariant } from "scmp-app/components/app-bar/types";
import type { Application } from "scmp-app/components/app-initializer/helpers";
import type { ArticleRenderType } from "scmp-app/components/article/article-render/types";
import { GameType } from "scmp-app/components/games/types";
import { SubSectionList as StyleSubSectionList } from "scmp-app/components/magazines-style/consts";
import { page as aboutMynews } from "scmp-app/pages/about-mynews";
import { page as article, pathname as articlePathname } from "scmp-app/pages/article";
import { page as author, pathname as authorPathname } from "scmp-app/pages/author";
import { page as authors, pathname as authorsPathname } from "scmp-app/pages/authors";
import {
  page as taiPoTragedyEPaperCollection,
  pathname as taiPoTragedyEPaperCollectionPathname,
} from "scmp-app/pages/epaper/tai-po-fire-tragedy-e-paper-collection";
import { page as games, pathname as gamesPathname } from "scmp-app/pages/games";
import { page as gamesCrossword } from "scmp-app/pages/games/crossword";
import {
  page as gameCrossword,
  pathname as gameCrosswordPathname,
} from "scmp-app/pages/games/crossword/[date]";
import { page as gamesCrosswordArchive } from "scmp-app/pages/games/crossword/archive";
import {
  page as gamesMiniSudoku,
  pathname as gamesMiniSudokuPathname,
} from "scmp-app/pages/games/mini-sudoku";
import {
  page as gameMiniSudoku,
  pathname as gameMiniSudokuPathname,
} from "scmp-app/pages/games/mini-sudoku/[date]";
import {
  page as gamesMiniSudokuArchive,
  pathname as gamesMiniSudokuArchivePathname,
} from "scmp-app/pages/games/mini-sudoku/archive";
import {
  page as gamesWordSearch,
  pathname as gamesWordSearchPathname,
} from "scmp-app/pages/games/word-search";
import {
  page as gameWordSearch,
  pathname as gameWordSearchPathname,
} from "scmp-app/pages/games/word-search/[date]";
import {
  page as gamesWordSearchArchive,
  pathname as gamesWordSearchArchivePathname,
} from "scmp-app/pages/games/word-search/archive";
import { page as home } from "scmp-app/pages/home";
import { page as live, pathname as livePathname } from "scmp-app/pages/live";
import { page as menu, pathname as menuPathname } from "scmp-app/pages/menu";
import { page as mynews, pathname as mynewsPathname } from "scmp-app/pages/mynews";
import { page as newsletters, pathname as newslettersPathname } from "scmp-app/pages/newsletters";
import {
  page as newslettersPreview,
  pathname as newslettersPreviewPathname,
} from "scmp-app/pages/newsletters/preview";
import { page as page, pathname as pagePathname } from "scmp-app/pages/page";
import { page as plus, pathname as plusPathname } from "scmp-app/pages/plus";
import { page as plusAgenda, pathname as plusAgendaPathname } from "scmp-app/pages/plus/agenda";
import { page as plusArchive, pathname as plusArchivePathname } from "scmp-app/pages/plus/archive";
import { page as plusCft, pathname as plusCftPathname } from "scmp-app/pages/plus/cft";
import {
  page as plusLearnMore,
  pathname as plusLearnMorePathname,
} from "scmp-app/pages/plus/learnmore";
import { page as plusSearch, pathname as plusSearchPathname } from "scmp-app/pages/plus/search";
import { page as podcast, pathname as podcastPathname } from "scmp-app/pages/podcast";
import { page as podcasts, pathname as podcastsPathname } from "scmp-app/pages/podcasts";
import {
  page as printArticle,
  pathname as printArticlePathname,
} from "scmp-app/pages/print-article";
import { page as search, pathname as searchPathname } from "scmp-app/pages/search";
import { page as section, pathname as sectionPathname } from "scmp-app/pages/section";
import type { Variant as SectionVariant } from "scmp-app/pages/section/enums";
import { getSectionRelatedSectionsQueueName } from "scmp-app/pages/section/helpers";
import { page as specialIndex } from "scmp-app/pages/special-index";
import {
  page as storyCard,
  styleStaticPathname as storyStyleStaticPathname,
} from "scmp-app/pages/story-card";
import { page as subSection, pathname as subSectionPathname } from "scmp-app/pages/sub-section";
import { page as topic, pathname as topicPathname } from "scmp-app/pages/topic";
import {
  page as topicCollection,
  pathname as topicCollectionPathname,
} from "scmp-app/pages/topic-collection";
import { page as topics, pathname as topicsPathname } from "scmp-app/pages/topics";
import { page as video, pathname as videoPathname } from "scmp-app/pages/video";
import {
  page as videoSection,
  pathname as videoSectionPathname,
} from "scmp-app/pages/video-section";

const pages = {
  aboutMynews,
  article,
  author,
  authors,
  gameCrossword,
  gameMiniSudoku,
  games,
  gamesCrossword,
  gamesCrosswordArchive,
  gamesMiniSudoku,
  gamesMiniSudokuArchive,
  gamesWordSearch,
  gamesWordSearchArchive,
  gameWordSearch,
  home,
  live,
  menu,
  mynews,
  newsletters,
  newslettersPreview,
  page,
  plus,
  plusAgenda,
  plusArchive,
  plusCft,
  plusLearnMore,
  plusSearch,
  podcast,
  podcasts,
  printArticle,
  search,
  section,
  specialIndex,
  storyCard,
  subSection,
  taiPoTragedyEPaperCollection,
  topic,
  topicCollection,
  topics,
  video,
  videoSection,
};

type StaticRouteFunction = (context: {
  params: Record<string, string>;
  query: Record<string, string>;
}) => {
  asPath?: string;
  cacheMaxAge?: number;
  pathname: string;
  query?: Record<string, string>;
};

const statics: Record<string, StaticRouteFunction> = {
  "/": () => ({
    ...pages.home.route({ edition: Edition.International, slide: "main" }),
    cacheMaxAge: 60,
  }),
  "/about-mynews": () => ({
    ...pages.aboutMynews.route({}),
  }),
  "/asia": () => ({
    ...pages.home.route({ edition: Edition.Asia, slide: "main" }),
    cacheMaxAge: 60,
  }),
  "/epaper/tai-po-fire-tragedy-e-paper-collection": () => ({
    ...pages.taiPoTragedyEPaperCollection.route({}),
    cacheMaxAge: 60,
  }),
  "/explained": () => ({
    ...pages.specialIndex.route({
      pageType: PageType.Series,
      queueName: `section_top_${data.section.explained.entityId}`,
    }),
    cacheMaxAge: 60,
  }),
  "/games/crosswords-cryptic": () =>
    pages.gamesCrossword.route({
      gameType: GameType.Cryptic,
    }),
  "/games/crosswords-cryptic/archive": () =>
    pages.gamesCrosswordArchive.route({
      gameType: GameType.Cryptic,
    }),
  "/games/crosswords-quick": () =>
    pages.gamesCrossword.route({
      gameType: GameType.Quick,
    }),
  "/games/crosswords-quick/archive": () =>
    pages.gamesCrosswordArchive.route({
      gameType: GameType.Quick,
    }),
  [gamesPathname]: () => pages.games.route({}),
  [gamesWordSearchArchivePathname]: () => pages.gamesWordSearchArchive.route({}),
  [gamesWordSearchPathname]: () => pages.gamesWordSearch.route({}),
  [mynewsPathname]: () => ({
    ...pages.mynews.route({}),
    cacheMaxAge: 60,
  }),
  [videoPathname]: () => ({
    ...pages.video.route({}),
    cacheMaxAge: 60,
  }),
  ...(config.game.enabled
    ? {
        [gamesMiniSudokuArchivePathname]: () => pages.gamesMiniSudokuArchive.route({}),
        [gamesMiniSudokuPathname]: () => pages.gamesMiniSudoku.route({}),
      }
    : {}),
  "/hk": () => ({
    ...pages.home.route({ edition: Edition.HongKong, slide: "main" }),
    cacheMaxAge: 60,
  }),
  // Treat drupal video sections as sections since content service does not include video section
  "/scmp-films": () => ({
    ...pages.specialIndex.route({
      pageType: PageType.Series,
      queueName: `video_section_picks_${data.section.scmpFilms.entityId}`,
    }),
    cacheMaxAge: 60,
  }),
  "/scmp-spotlight": () => ({
    ...pages.specialIndex.route({
      pageType: PageType.Series,
      queueName: "scmp_spotlight_top",
    }),
    cacheMaxAge: 60,
  }),
  "/series": () => ({
    ...pages.specialIndex.route({
      pageType: PageType.Series,
      queueName: "scmp_series_top",
    }),
    cacheMaxAge: 60,
  }),
  [`${searchPathname}/:q?`]: context =>
    pages.search.route({ q: context.query.q ?? context.params.q }),
  ...StyleSubSectionList.reduce<Record<string, StaticRouteFunction>>(
    (staticsRoutes, subSection) => {
      staticsRoutes[`${storyStyleStaticPathname}/${subSection}`] = () => ({
        ...pages.storyCard.route({ subSection }),
        cacheMaxAge: 60,
      });
      return staticsRoutes;
    },
    {},
  ),
  "/talking-post": () => ({
    ...pages.specialIndex.route({
      pageType: PageType.Series,
      queueName: `video_section_picks_${data.section.talkingPost.entityId}`,
    }),
    cacheMaxAge: 60,
  }),
  [`${plusSearchPathname}/:q?`]: context =>
    pages.plusSearch.route({
      nodeId: context.query.nodeId,
      q: context.query.q ?? context.params.q,
    }),
  [authorsPathname]: context => pages.authors.route({ char: context.query.char }),
  [livePathname]: () => ({ ...pages.live.route({}), cacheMaxAge: 60 }),
  [menuPathname]: () => pages.menu.route({}),
  [newslettersPathname]: context => ({
    ...pages.newsletters.route({ embed: context.query.embed }),
    cacheMaxAge: 300,
  }),
  [plusAgendaPathname]: context => ({
    ...pages.plusAgenda.route({
      articleId: context.query.articleId,
      entityId: context.query.entityId,
      nodeId: context.query.nodeId,
      startDate: context.query.startDate,
    }),
    cacheMaxAge: 60,
  }),
  [plusArchivePathname]: context => ({
    ...pages.plusArchive.route({
      nodeId: context.query.nodeId,
    }),
    cacheMaxAge: 60,
  }),
  [plusCftPathname]: () => pages.plusCft.route({}),
  [plusLearnMorePathname]: () => pages.plusLearnMore.route({}),
  [plusPathname]: context => ({
    ...pages.plus.route({
      articleId: context.query.articleId,
      display: context.query.display,
      nodeId: context.query.nodeId,
    }),
    cacheMaxAge: 60,
  }),
  [podcastsPathname]: () => pages.podcasts.route({}),
  [topicsPathname]: context => pages.topics.route({ char: context.query.char }),
};

interface DynamicRoute {
  cacheMaxAge?: number;
  pathnames: string[];
  prefix?: string[];
  route: DynamicRouteFunction;
}

type DynamicRouteFunction = {
  (entityId: string, context?: DynamicRouteFunctionContext): DynamicRouteFunctionResult;
};

type DynamicRouteFunctionContext = {
  advertZone?: string;
  appBarVariant?: AppBarVariant;
  application?: Application;
  articleRenderType?: ArticleRenderType;
  asPath?: string;
  entityUuid?: string;
  fullSectionPath?: string[];
  isBot?: string;
  isPlusInlinePaywall?: boolean;
  matchedPrefix: OrUndefined<string>;
  name?: null | string;
  nodeId?: null | string;
  params: Record<string, string>;
  parentSection?: null | string;
  query: Record<string, string>;
  sectionVariant?: SectionVariant;
  startDate?: string;
};

type DynamicRouteFunctionResult = {
  pathname: string;
  query?: Record<string, string>;
};

export function isStaticRoute(url: string) {
  return Object.keys(routes.statics).some(staticRoute => staticRoute.startsWith(url));
}

const dynamics: Record<string, DynamicRoute> = {
  ...(config.game.enabled
    ? {
        GameMiniSudoku: {
          cacheMaxAge: 60,
          pathnames: [gameMiniSudokuPathname],
          route(entityId) {
            return pages.gameMiniSudoku.route({ date: entityId });
          },
        },
      }
    : {}),
  Article: {
    pathnames: [articlePathname, printArticlePathname],
    prefix: [...pages.printArticle.prefix],
    route(entityId, context) {
      const matchedPrefix = context?.matchedPrefix;
      const commentID = context?.query.commentID;
      const entityUuid = context?.entityUuid ?? "";
      const appBarVariant = context?.appBarVariant;
      const application = context?.application;
      const isBot = context?.isBot;
      const renderType = context?.articleRenderType;
      const share = context?.query.share ?? "";

      return matchedPrefix && pages.printArticle.prefix.includes(matchedPrefix)
        ? pages.printArticle.route({ entityId })
        : pages.article.route({
            appBarVariant,
            application,
            commentID,
            entityId,
            entityUuid,
            isBot,
            renderType,
            share,
          });
    },
  },
  Author: {
    cacheMaxAge: 60,
    pathnames: [authorPathname],
    route(entityId) {
      return pages.author.route({ entityId });
    },
  },
  Collection: {
    cacheMaxAge: 60,
    pathnames: [topicCollectionPathname],
    route: (entityId, context) => {
      const advertZone = context?.advertZone ?? "";
      return pages.topicCollection.route({ advertZone, entityId });
    },
  },
  GameCrossword: {
    cacheMaxAge: 60,
    pathnames: [gameCrosswordPathname],
    route(entityId, context) {
      return pages.gameCrossword.route({
        entityId,
        gameTitle: context?.params?.gameTitle,
        gameType: context?.params?.gameType as GameType,
      });
    },
  },
  GameWordSearch: {
    cacheMaxAge: 60,
    pathnames: [gameWordSearchPathname],
    route(entityId) {
      return pages.gameWordSearch.route({ date: entityId });
    },
  },
  NewsAgenda: {
    pathnames: [plusAgendaPathname],
    route(entityId, context) {
      return pages.plusAgenda.route({
        entityId,
        startDate: context?.startDate,
      });
    },
  },
  Newsletter: {
    cacheMaxAge: 300,
    pathnames: [newslettersPreviewPathname],
    route(entityId, context) {
      const embed = context?.query.embed;
      return pages.newslettersPreview.route({ embed, entityId });
    },
  },
  Page: {
    pathnames: [pagePathname],
    route(entityId, context) {
      const appBarVariant = context?.appBarVariant;
      const application = context?.application;
      return pages.page.route({ appBarVariant, application, entityId });
    },
  },
  Plus: {
    cacheMaxAge: 60,
    pathnames: [plusPathname],
    route(entityId, context) {
      return pages.plus.route({
        articleId: entityId,
        display: context?.query.display,
        isPlusInlinePaywall: context?.isPlusInlinePaywall,
        nodeId: context?.nodeId ?? "",
      });
    },
  },
  Podcast: {
    pathnames: [podcastPathname],
    route(entityId) {
      return pages.podcast.route({ entityId });
    },
  },
  PodcastSection: {
    pathnames: [podcastPathname],
    route(entityId) {
      return pages.podcast.route({ entityId });
    },
  },
  Section: {
    cacheMaxAge: 60,
    pathnames: [sectionPathname],
    route(entityId, context) {
      const advertZone = context?.advertZone ?? "";
      const application = context?.application;
      const appBarVariant = context?.appBarVariant;
      const name = context?.name ?? "";
      const parentSection = context?.parentSection ?? "";
      const fullSectionPath = context?.fullSectionPath ?? [];
      const sectionVariant = context?.sectionVariant;

      return pages.section.route({
        advertZone,
        appBarVariant,
        application,
        entityId,
        name,
        parentSection,
        relatedSectionsQueueName: getSectionRelatedSectionsQueueName(fullSectionPath),
        sectionVariant,
      });
    },
  },
  SubSection: {
    cacheMaxAge: 60,
    pathnames: [subSectionPathname],
    route: (entityId, context) => {
      const advertZone = context?.advertZone ?? "";
      const appBarVariant = context?.appBarVariant;
      const name = context?.name ?? "";
      const parentSection = context?.parentSection ?? "";
      const fullSectionPath = context?.fullSectionPath ?? [];
      const sectionVariant = context?.sectionVariant;
      const asPath = context?.asPath;

      return pages.subSection.route({
        advertZone,
        appBarVariant,
        asPath,
        entityId,
        name,
        parentSection,
        relatedSectionsQueueName: getSectionRelatedSectionsQueueName(fullSectionPath),
        sectionVariant,
      });
    },
  },
  TaiPoTragedyEPaperCollection: {
    cacheMaxAge: 60,
    pathnames: [taiPoTragedyEPaperCollectionPathname],
    route() {
      return pages.taiPoTragedyEPaperCollection.route({});
    },
  },
  Topic: {
    cacheMaxAge: 60,
    pathnames: [topicPathname],
    route: (entityId, context) => {
      const advertZone = context?.advertZone ?? "";
      return pages.topic.route({ advertZone, entityId });
    },
  },
  Video: {
    pathnames: [articlePathname],
    route(entityId, context) {
      const commentID = context?.query.commentID;
      const entityUuid = context?.entityUuid ?? "";
      const appBarVariant = context?.appBarVariant;
      const application = context?.application;
      const isBot = context?.isBot;
      const renderType = context?.articleRenderType;
      const share = context?.query.share ?? "";
      return pages.article.route({
        appBarVariant,
        application,
        commentID,
        entityId,
        entityUuid,
        isBot,
        isVideo: "true",
        renderType,
        share,
      });
    },
  },
  VideoSection: {
    cacheMaxAge: 60,
    pathnames: [videoSectionPathname],
    route(entityId, context) {
      const entityUuid = context?.entityUuid ?? "";
      return pages.videoSection.route({ entityId, entityUuid });
    },
  },
};

// These redirects are scoped to the pwa application level for server side redirects.
// Client side should not need to handle these as there should not be any route define with old path.
const applicationRedirects = {
  "/frontpage/hk": "/hk",
  "/frontpage/international": "/",
  "/home": "/",
  "/newsletter": "/newsletters",
  "/us": "/",
};

// These redirects are defined in the drupal.
// Client side should route these paths to server to resolve for the actual redirect value.
const drupalRedirects = [
  "/topics/coronavirus-outbreak",
  "/topics/coronavirus-pandemic",
  "/topics/explorers-tomorrow",
  "/comment/harrys-view",
];

export function isDrupalRedirect(url: string) {
  return drupalRedirects.some(redirect => url.startsWith(redirect));
}

// These patterns are handled by different applications with routing at ingress
const internals = [
  "/sport/racing",
  "/infographic",
  "/photos",
  "/cooking",
  "/yp",
  "/china-internet-report",
  "/knowledge",
];

// These patterns that belongs to external are specially handled by pwa internally
const excludedInternals = [
  "/knowledge/us-china-relations",
  "/knowledge/china-macro-economy",
  "/infographics",
];

export function isInternalDomainRoute(url: string) {
  return (
    internals.some(internal => url.startsWith(internal)) &&
    !excludedInternals.some(excluded => url.startsWith(excluded))
  );
}

const rejects = ["/inkstone"];

export const routes = {
  applicationRedirects,
  drupalRedirects,
  dynamics,
  excludedInternals,
  internals,
  rejects,
  statics,
};
